import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import styles from "../../assets/styles/Homepage/KundaliniCourse.module.css";
import kundaliniImg from "../../assets/images/a/IMAGE.webp"; // replace with actual image
import { useNavigate } from "react-router-dom";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
/* ======================== */
/* Framer Motion Variants   */
/* ======================== */

const sectionVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, x: -40, scale: 0.95 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const contentVariant = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
      staggerChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/* ======================== */
/* Course Data              */
/* ======================== */

const API_BASE = `${ENV_API_BASE_URL}/api`;

const DEFAULT_COURSES = [
  {
    title: "100-Hour Kundalini Yoga",
    label: "Foundation Course",
    price: "599",
    duration: "14 Days",
    date: "2nd of every month",
    badge: "Beginner",
    detailsRoute: "/100-hour-kundalini-yoga",
  },
  {
    title: "200-Hour Kundalini Yoga",
    label: "Teacher Training Course",
    price: "980",
    duration: "24 Days",
    date: "2nd of every month",
    badge: "Popular",
    detailsRoute: "/200-hour-kundalini-yoga",
  },
  {
    title: "300-Hour Kundalini Yoga",
    label: "Advanced Teacher Training",
    price: "1150",
    duration: "28 Days",
    date: "2nd of every month",
    badge: "Advanced",
    detailsRoute: "/300-hour-kundalini-yoga",
  },
];

/* ======================== */
/* Main Section             */
/* ======================== */

const KundaliniCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(DEFAULT_COURSES);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses`, {
          params: { homeSection: "kundalini" },
        });
        const items = res.data?.data || [];
        if (!items.length) return;

        const mapped = items.map((course) => {
          const details = course.kundalini || {};
          return {
            title: details.title || course.title || "",
            label: details.label || course.shortTitle || "",
            price: details.price || course.card?.price || "",
            duration: details.duration || course.duration || "",
            date: details.date || course.home?.date || "",
            badge: details.badge || course.home?.badge || "Beginner",
            detailsRoute: details.detailsRoute || course.legacyPath || (course.slug ? `/course/${course.slug}` : "/BookingForm"),
          };
        });

        setCourses(mapped);
      } catch (error) {
        console.error("Failed to load kundalini courses", error);
      }
    };

    fetchCourses();
  }, []);

  return (
    <motion.section
      className={styles.section}
      variants={sectionVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className={`container ${styles.cardWrapper}`}>
        <div className={styles.card}>

          {/* Left Image */}
          <motion.div className={styles.imageBox} variants={imageVariant}>
            <img src={kundaliniImg} alt="Kundalini Yoga Teacher Training" />
          </motion.div>

          {/* Right Content */}
          <motion.div className={styles.contentBox} variants={contentVariant}>

            {/* Header */}
            <motion.h2 className={styles.mainTitle} variants={fadeUp}>
               200 Hour Hatha Kundalini Yoga Teacher Training Course
            </motion.h2>
            <motion.p className={styles.mainSubtitle} variants={fadeUp}>
              Teacher Training Courses
            </motion.p>
            <motion.p className={styles.mainDescription} variants={fadeUp}>
              Kundalini Yoga is a powerful and transformative practice that awakens
              the dormant energy at the base of the spine, guiding it upward through
              the chakras to achieve higher states of consciousness. Whether you are
              a complete beginner or an experienced practitioner, we have a course
              designed to support your journey.
            </motion.p>

            <motion.hr className={styles.divider} variants={fadeUp} />

            {/* Three Course Columns */}
            <motion.div className={styles.coursesRow} variants={fadeUp}>
              {courses.map((course, index) => (
                <React.Fragment key={course.title}>
                  <div className={styles.courseCol}>

                    {/* Badge */}
                    <span className={`${styles.badge} ${styles[`badge${course.badge}`]}`}>
                      {course.badge}
                    </span>

                    <h3 className={styles.courseTitle}>{course.title}</h3>
                    <p className={styles.courseLabel}>{course.label}</p>

                    <div className={styles.price}>
                      <span></span>{course.price}
                    </div>

                    <div className={styles.infoRow}>
                      <span className={styles.icon}>📅</span>
                      <span><strong>Duration:</strong> {course.duration}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.icon}>📅</span>
                      <span><strong>Date:</strong> {course.date}</span>
                    </div>

                    <div className={styles.btnGroup}>
                      <motion.button
                        className={styles.outlineBtn}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate(course.detailsRoute)}
                      >
                        More Details
                      </motion.button>
                      <motion.button
                        className={styles.fillBtn}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate("/BookingForm")}
                      >
                        Book Now
                      </motion.button>
                    </div>
                  </div>

                  {/* Vertical divider between columns */}
                  {index < courses.length - 1 && (
                    <div className={styles.verticalDivider} />
                  )}
                </React.Fragment>
              ))}
            </motion.div>

          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default KundaliniCourses;
